/**
 * Turns the guide data into actual itinerary constraints.
 *
 * Showing "3650m" on a card helps. Silently building someone a trip that
 * takes a five-year-old to 4,441m does not. This is where the guide facts
 * stop being decoration and start removing stops from the plan.
 *
 * Exclusions are returned with a reason rather than applied invisibly — the
 * user asked to know the pitfalls, so a stop we drop is worth surfacing.
 */
import { GUIDES } from "./guides.js";
import type { Experience } from "./types.js";

export interface PartyConstraints {
  kids?: boolean;
  /**
   * Age of the youngest traveller. When kids is set and this is unknown we
   * assume 5 — conservative on purpose, because guessing high is the failure
   * mode that hurts someone.
   */
  youngestAge?: number;
  /** Hard ceiling in metres, for anyone who wants to cap altitude outright. */
  maxAltitude?: number;
}

export interface Exclusion {
  id: string;
  en: string;
  cn: string;
  reasonEn: string;
  reasonCn: string;
}

/**
 * Altitude at which we stop planning for young children.
 *
 * Travel-medicine guidance is consistently cautious about taking young
 * children rapidly to high elevation, and symptoms are harder to recognise in
 * a child who cannot describe them. This is a planning default, not medical
 * advice — the UI says to ask a doctor.
 */
export const KID_ALTITUDE_LIMIT = 3000;

const DEFAULT_YOUNGEST = 5;

/** The altitude rule is about young children, not teenagers. */
export const YOUNG_CHILD_AGE = 12;

/** Returns why this stop does not work for the party, or null if it does. */
export function excludedBy(e: Experience, c: PartyConstraints): Exclusion | null {
  const g = GUIDES[e.id];
  if (!g) return null;
  const base = { id: e.id, en: e.en, cn: e.cn };

  if (c.maxAltitude !== undefined && g.altitude !== undefined && g.altitude > c.maxAltitude) {
    return {
      ...base,
      reasonEn: `${g.altitude}m is above your ${c.maxAltitude}m limit`,
      reasonCn: `海拔 ${g.altitude} 米，超过你设定的 ${c.maxAltitude} 米上限`,
    };
  }

  if (!c.kids) return null;
  const age = c.youngestAge ?? DEFAULT_YOUNGEST;

  if (age < YOUNG_CHILD_AGE && g.altitude !== undefined && g.altitude >= KID_ALTITUDE_LIMIT) {
    return {
      ...base,
      reasonEn: `${g.altitude}m — too high to plan around with a young child`,
      reasonCn: `海拔 ${g.altitude} 米 —— 带幼童不宜安排`,
    };
  }

  if (g.minAge !== undefined && age < g.minAge) {
    return {
      ...base,
      reasonEn: `suggested for ages ${g.minAge}+`,
      reasonCn: `建议 ${g.minAge} 岁以上`,
    };
  }

  return null;
}

export const isSuitable = (e: Experience, c: PartyConstraints): boolean =>
  excludedBy(e, c) === null;
